/**
 * AdminOpsAlerts.js — Command Centre "Ops Radar":
 * 1. System Alerts feed (journey failures & critical errors) with acknowledge
 * 2. Deletion Window — families inside the 90-day Delete Account lifecycle
 */
import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Radar, Loader2, RefreshCw, CheckCircle, AlertTriangle, HeartCrack } from 'lucide-react';
import { apiClient } from '../../api/client';

const SEVERITY = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  info: 'bg-sky-100 text-sky-700 border-sky-200',
};

export default function AdminOpsAlerts() {
  const [alerts, setAlerts] = useState(null);
  const [families, setFamilies] = useState(null);
  const [acking, setAcking] = useState(null);

  const load = async () => {
    const [a, d] = await Promise.all([
      apiClient.request('/api/admin/system-alerts').catch(() => ({ alerts: [], unresolved_count: 0 })),
      apiClient.request('/api/admin/deletion-window').catch(() => ({ families: [], count: 0 })),
    ]);
    setAlerts(a);
    setFamilies(d);
  };
  useEffect(() => { load(); }, []);

  const ack = async (id) => {
    setAcking(id);
    try {
      await apiClient.request(`/api/admin/system-alerts/${id}/ack`, { method: 'POST' });
      await load();
    } catch (e) { alert(e?.message || 'Failed'); }
    setAcking(null);
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl" data-testid="admin-ops-alerts-page">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
              <Radar className="text-rose-500" size={24} /> Ops Radar
            </h1>
            <p className="text-sm text-gray-500">Critical alerts and families in the deletion window — the things that must never go unseen.</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700" data-testid="ops-radar-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {!alerts || !families ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rose-500" size={30} /></div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6" data-testid="system-alerts-panel">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="font-bold text-gray-900 text-sm flex items-center gap-2">
                  <AlertTriangle size={15} className="text-red-500" /> System Alerts
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${alerts.unresolved_count > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`} data-testid="unresolved-count">
                  {alerts.unresolved_count} open
                </span>
              </div>
              {alerts.alerts.length === 0 ? (
                <p className="p-6 text-sm text-gray-400 flex items-center gap-2" data-testid="alerts-empty">
                  <CheckCircle size={16} className="text-emerald-500" /> All clear — no system alerts on record.
                </p>
              ) : alerts.alerts.map(a => (
                <div key={a.alert_id} className={`px-4 py-3 border-b border-gray-50 flex items-start justify-between gap-3 ${a.resolved ? 'opacity-50' : ''}`} data-testid={`alert-${a.alert_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${SEVERITY[a.severity] || SEVERITY.info}`}>
                        {(a.severity || 'info').toUpperCase()}
                      </span>
                      <span className="font-semibold text-gray-900 text-sm">{(a.type || '').replaceAll('_', ' ')}</span>
                      {a.user_label && <span className="text-xs text-gray-500">· {a.user_label}</span>}
                      <span className="text-[11px] text-gray-400">{(a.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    </div>
                    {a.error && <p className="text-xs text-gray-600 mt-1 truncate">{a.error}</p>}
                  </div>
                  {!a.resolved && (
                    <button onClick={() => ack(a.alert_id)} disabled={acking === a.alert_id}
                      className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-bold hover:bg-gray-700 disabled:opacity-50"
                      data-testid={`ack-${a.alert_id}`}>
                      {acking === a.alert_id ? '…' : 'Acknowledge'}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="deletion-window-panel">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="font-bold text-gray-900 text-sm flex items-center gap-2">
                  <HeartCrack size={15} className="text-rose-500" /> Deletion Window (90-day)
                </span>
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold" data-testid="deletion-count">
                  {families.count} famil{families.count === 1 ? 'y' : 'ies'}
                </span>
              </div>
              {families.families.length === 0 ? (
                <p className="p-6 text-sm text-gray-400" data-testid="deletion-empty">No families are in the deletion window. 💚</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                      <th className="px-4 py-2">Parent</th><th className="px-4 py-2">Requested</th>
                      <th className="px-4 py-2">Days Left</th><th className="px-4 py-2">Teens</th><th className="px-4 py-2">Nudges Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {families.families.map(f => (
                      <tr key={f.user_id} className="border-b border-gray-50" data-testid={`deletion-row-${f.user_id}`}>
                        <td className="px-4 py-2.5">
                          <p className="font-semibold text-gray-900">{f.name || '—'}</p>
                          <p className="text-xs text-gray-500">{f.email}</p>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 text-xs">{(f.requested_at || '').slice(0, 10)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`font-bold ${f.days_left !== null && f.days_left <= 7 ? 'text-red-600' : 'text-gray-900'}`}>
                            {f.days_left ?? '—'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{f.linked_teens}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{f.nudges_sent.length ? f.nudges_sent.join(', ') : 'none yet'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
